
import React, { createContext, useState, useEffect, useContext } from 'react';
import { AuthResponse, Session, User } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { Loader2 } from 'lucide-react';
import { AuthEvent, AuthEventType } from '@/lib/types/auth';
import { UserProfile } from '@/types/user';

interface AuthState {
  isAuthenticating: boolean;
  isInitialized: boolean;
  isLoadingProfile: boolean;
  error: Error | null;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  userProfile: UserProfile | null;
  signIn: (email: string, password: string) => Promise<AuthResponse>;
  signUp: (email: string, password: string, userData?: Record<string, unknown>) => Promise<AuthResponse>;
  signOut: () => Promise<void>;
  deleteUser: (confirmationEmail: string) => Promise<{ error: Error | null }>;
  isProfileComplete: boolean;
  refreshSession: () => Promise<void>;
  loading: boolean;
  handleRecoveryToken: (token: string) => Promise<void>;
  handleReturnUrl: (url: string) => void;
  returnUrl: string;
  linkGoogleAccount: (redirectPath?: string) => Promise<{ error: Error | null }>;
  unlinkIdentity: (identity: any) => Promise<{ error: Error | null }>;
  getUserIdentities: () => Promise<{ data: any[] | null; error: Error | null }>;
}

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

export const useAuth = () => useContext(AuthContext);

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [isProfileComplete, setIsProfileComplete] = useState(false);
  const [returnUrl, setReturnUrl] = useState<string>('/');
  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticating: true,
    isInitialized: false,
    isLoadingProfile: false,
    error: null,
  });

  const updateAuthState = (updates: Partial<AuthState>) => {
    setAuthState(prev => ({ ...prev, ...updates }));
  };

  const fetchUserProfile = async (userId: string): Promise<UserProfile | null> => {
    try {
      updateAuthState({ isLoadingProfile: true });
      const { data: profile, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      if (error) {
        console.error('[AuthContext] Error fetching user profile:', error);
        return null;
      }

      const isComplete = profile && profile.firstname && profile.lastname;
      setIsProfileComplete(!!isComplete);
      setUserProfile(profile);
      return profile;
    } catch (error) {
      console.error('[AuthContext] Error in fetchUserProfile:', error);
      return null;
    } finally {
      updateAuthState({ isLoadingProfile: false });
    }
  };

  const handleAuthChange = async (event: AuthEventType, newSession: Session | null) => {
    console.log('[AuthContext] Auth event:', event, 'Session exists:', !!newSession);
    
    updateAuthState({ isAuthenticating: true });
    
    try {
      switch (event) {
        case 'SIGNED_IN':
          if (newSession?.user) {
            setSession(newSession);
            setUser(newSession.user);
            // Defer profile fetching to avoid potential deadlocks
            setTimeout(() => {
              fetchUserProfile(newSession.user.id);
            }, 0);
          }
          break;
        
        case 'TOKEN_REFRESHED':
          if (newSession?.user) {
            setSession(newSession);
            setUser(newSession.user);
            if (!userProfile) {
              setTimeout(() => {
                fetchUserProfile(newSession.user.id);
              }, 0);
            }
          }
          break;
        
        case 'SIGNED_OUT':
          setSession(null);
          setUser(null);
          setUserProfile(null);
          setIsProfileComplete(false);
          break;
        
        case 'USER_UPDATED':
          if (newSession?.user) {
            setUser(newSession.user);
            setSession(newSession);
          }
          break;
      }
    } finally {
      updateAuthState({ isAuthenticating: false });
    }
  };

  const signIn = async (email: string, password: string): Promise<AuthResponse> => {
    updateAuthState({ isAuthenticating: true });
    try {
      const response = await supabase.auth.signInWithPassword({ email, password });
      return response;
    } finally {
      updateAuthState({ isAuthenticating: false });
    }
  };

  const signUp = async (email: string, password: string, userData: Record<string, unknown> = {}): Promise<AuthResponse> => {
    updateAuthState({ isAuthenticating: true });
    try {
      const response = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: userData,
        },
      });
      return response;
    } finally {
      updateAuthState({ isAuthenticating: false });
    }
  };

  const signOut = async (): Promise<void> => {
    updateAuthState({ isAuthenticating: true });
    try {
      await supabase.auth.signOut();
    } finally {
      updateAuthState({ isAuthenticating: false });
    }
  };

  const deleteUser = async (confirmationEmail: string): Promise<{ error: Error | null }> => {
    updateAuthState({ isAuthenticating: true });
    try {
      if (!user) {
        throw new Error('No user logged in');
      }

      // Get the current session to include the auth token
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('No active session');
      }

      // Call the Edge Function to delete the user
      const response = await fetch(`${supabase.supabaseUrl}/functions/v1/delete-user`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          confirmationEmail
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to delete user account');
      }

      // User was successfully deleted, now sign out locally
      await supabase.auth.signOut();
      
      return { error: null };
    } catch (error) {
      console.error('[AuthContext] Error deleting user:', error);
      return { error: error as Error };
    } finally {
      updateAuthState({ isAuthenticating: false });
    }
  };

  const refreshSession = async (): Promise<void> => {
    console.log('[AuthContext] Refreshing session');
    try {
      const { data: { session: currentSession }, error } = await supabase.auth.getSession();
      
      if (error) {
        console.error('[AuthContext] Error refreshing session:', error);
        return;
      }
      
      if (currentSession) {
        setSession(currentSession);
        setUser(currentSession.user);
        if (currentSession.user) {
          // Defer profile fetching to avoid blocking
          setTimeout(() => {
            fetchUserProfile(currentSession.user.id);
          }, 0);
        }
      }
    } catch (error) {
      console.error('[AuthContext] Error refreshing session:', error);
    }
  };

  const handleRecoveryToken = async (token: string): Promise<void> => {
    console.log('[AuthContext] Handling recovery token:', token);
    // Recovery tokens are now handled by the Auth component directly
  };

  const handleReturnUrl = (url: string): void => {
    console.log('[AuthContext] Setting return URL:', url);
    setReturnUrl(url);
  };

  const linkGoogleAccount = async (redirectPath: string = '/profile'): Promise<{ error: Error | null }> => {
    try {
      const redirectTo = `${window.location.origin}${redirectPath}?linked=success`;
      console.log('[AuthContext] Linking Google account with redirect to:', redirectTo);
      console.log('[AuthContext] Current user:', user?.id);
      
      const { error } = await supabase.auth.linkIdentity({
        provider: 'google',
        options: {
          redirectTo,
          queryParams: {
            prompt: 'select_account'
          }
        }
      });
      
      if (error) {
        console.error('[AuthContext] Supabase linkIdentity error:', error);
        throw error;
      }
      
      console.log('[AuthContext] linkIdentity call successful');
      return { error: null };
    } catch (error) {
      console.error('[AuthContext] Error linking Google account:', error);
      return { error: error as Error };
    }
  };

  const unlinkIdentity = async (identity: any): Promise<{ error: Error | null }> => {
    try {
      console.log('[AuthContext] Unlinking identity:', identity);
      const { error } = await supabase.auth.unlinkIdentity(identity);
      
      if (error) throw error;
      
      return { error: null };
    } catch (error) {
      console.error('[AuthContext] Error unlinking identity:', error);
      return { error: error as Error };
    }
  };

  const getUserIdentities = async (): Promise<{ data: any[] | null; error: Error | null }> => {
    try {
      const { data, error } = await supabase.auth.getUserIdentities();
      
      if (error) throw error;
      
      return { data: data?.identities || [], error: null };
    } catch (error) {
      console.error('[AuthContext] Error fetching user identities:', error);
      return { data: null, error: error as Error };
    }
  };

  useEffect(() => {
    console.log('[AuthContext] Setting up auth state change handler');
    updateAuthState({ isAuthenticating: true, isInitialized: false });

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, changedSession) => {
      await handleAuthChange(event as AuthEventType, changedSession);
      if (!authState.isInitialized) {
        updateAuthState({ isInitialized: true });
      }
    });

    // Check for tokens in URL hash fragments or returnTo parameter (from mobile app)
    const checkUrlFragments = async () => {
      let tokenString = '';

      // Check direct hash fragments first
      const hash = window.location.hash;
      if (hash && hash.includes('access_token') && hash.includes('refresh_token')) {
        tokenString = hash.substring(1); // Remove # prefix
        console.log('[AuthContext] Found tokens in URL hash');
      } else {
        // Check returnTo parameter (SSR case)
        const urlParams = new URLSearchParams(window.location.search);
        const returnTo = urlParams.get('returnTo');
        if (returnTo && returnTo.includes('#access_token') && returnTo.includes('refresh_token')) {
          const hashIndex = returnTo.indexOf('#');
          if (hashIndex !== -1) {
            tokenString = returnTo.substring(hashIndex + 1); // Extract hash part and remove # prefix
            console.log('[AuthContext] Found tokens in returnTo parameter');
          }
        }
      }

      if (tokenString) {
        console.log('[AuthContext] Setting session from tokens - forcing override of existing session');
        try {
          // Parse token parameters
          const params = new URLSearchParams(tokenString);
          const accessToken = params.get('access_token');
          const refreshToken = params.get('refresh_token');

          if (accessToken && refreshToken) {
            // Check if there's an existing session
            const { data: { session: currentSession } } = await supabase.auth.getSession();

            if (currentSession) {
              // If there's an existing session, clear it first to ensure clean override
              console.log('[AuthContext] Clearing existing session before setting new tokens');
              await supabase.auth.signOut({ scope: 'local' });
            }

            console.log('[AuthContext] Setting session from URL tokens');

            // Use refreshSession method which works better for token-based auth
            const { data, error } = await supabase.auth.refreshSession({
              refresh_token: refreshToken,
            });

            if (error) {
              console.error('[AuthContext] Error setting session from URL:', error);

              // Fallback: Try setSession if refreshSession fails
              try {
                const { data: fallbackData, error: fallbackError } = await supabase.auth.setSession({
                  access_token: accessToken,
                  refresh_token: refreshToken,
                });

                if (fallbackError) {
                  console.error('[AuthContext] Fallback setSession also failed:', fallbackError);
                  return false;
                }

                if (fallbackData.session) {
                  setSession(fallbackData.session);
                  setUser(fallbackData.session.user);
                  if (fallbackData.session.user) {
                    setTimeout(() => {
                      fetchUserProfile(fallbackData.session.user.id);
                    }, 0);
                  }
                }
              } catch (fallbackErr) {
                console.error('[AuthContext] Failed to set session:', fallbackErr);
                return false;
              }
            } else {
              console.log('[AuthContext] Session set successfully from URL tokens');

              // Set the session immediately in our context to avoid race conditions
              if (data.session) {
                setSession(data.session);
                setUser(data.session.user);
                if (data.session.user) {
                  setTimeout(() => {
                    fetchUserProfile(data.session.user.id);
                  }, 0);
                }
              }
            }

            // Clean up URL - remove tokens from both hash and query params
            const cleanUrl = window.location.pathname;
            window.history.replaceState({}, '', cleanUrl);

            return true; // Indicate success
          }
        } catch (error) {
          console.error('[AuthContext] Error processing URL tokens:', error);
          return false;
        }
      }

      return false; // No tokens found
    };

    // Initial session check
    const checkInitialSession = async () => {
      try {
        // First check for URL tokens
        const tokensProcessed = await checkUrlFragments();

        // Only check existing session if no tokens were processed from URL
        if (!tokensProcessed) {
          const { data: { session: initialSession } } = await supabase.auth.getSession();
          if (initialSession) {
            setSession(initialSession);
            setUser(initialSession.user);
            if (initialSession.user) {
              setTimeout(() => {
                fetchUserProfile(initialSession.user.id);
              }, 0);
            }
          }
        }
        updateAuthState({ isInitialized: true, isAuthenticating: false });
      } catch (error) {
        console.error('[AuthContext] Error checking initial session:', error);
        updateAuthState({ isInitialized: true, isAuthenticating: false });
      }
    };

    checkInitialSession();

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const value = {
    user,
    session,
    userProfile,
    signIn,
    signUp,
    signOut,
    deleteUser,
    isProfileComplete,
    refreshSession,
    loading: authState.isAuthenticating || authState.isLoadingProfile,
    handleRecoveryToken,
    handleReturnUrl,
    returnUrl,
    linkGoogleAccount,
    unlinkIdentity,
    getUserIdentities
  };

  const isLoading = !authState.isInitialized || authState.isAuthenticating;

  return (
    <AuthContext.Provider value={value}>
      {isLoading ? (
        <div className="flex items-center justify-center h-screen">
          <Loader2 className="animate-spin text-primary" />
        </div>
      ) : (
        children
      )}
    </AuthContext.Provider>
  );
};
